import { QrCode, QrEcc } from "./qrcodegen";

/** The light margin QR readers expect around the code, in modules (the standard asks for 4). */
export const QR_QUIET_ZONE = 4;

export interface QrSvgPath {
  /** `0 0 n n`, where n is the code's size plus the quiet zone on both sides. */
  readonly viewBox: string;
  /** One path of the dark modules: each run of dark modules in a row is one rectangle. */
  readonly path: string;
  /** Modules across, quiet zone included. */
  readonly extent: number;
}

/**
 * The dark modules of a QR code as one SVG path, ready for `<path d=…>` on a light plate. Runs
 * of dark modules in a row are merged into one rectangle, so the path stays short for a phone
 * link (a version 5 to 7 code). No raster, no data URL and no inline style.
 */
export function qrSvgPath(code: Pick<QrCode, "size" | "getModule">, border = QR_QUIET_ZONE) {
  if (!Number.isInteger(border) || border < 0)
    throw new RangeError("Border must be a whole number");
  const parts: string[] = [];
  for (let y = 0; y < code.size; y++) {
    let x = 0;
    while (x < code.size) {
      if (!code.getModule(x, y)) {
        x += 1;
        continue;
      }
      let run = 1;
      while (x + run < code.size && code.getModule(x + run, y)) run += 1;
      parts.push(`M${x + border} ${y + border}h${run}v1h-${run}z`);
      x += run;
    }
  }
  const extent = code.size + border * 2;
  return { viewBox: `0 0 ${extent} ${extent}`, path: parts.join(""), extent } satisfies QrSvgPath;
}

/**
 * The QR code for a patient link, at error correction level M (about 15% of the code may be
 * damaged or covered and it still reads), which keeps a 100-character link at version 5 to 7.
 */
export function patientLinkQrCode(url: string): QrCode {
  return QrCode.encodeText(url, QrEcc.MEDIUM);
}
